/**
 * @fileoverview AR-805 — read-only Classroom assignment state of an activity
 * (PRD G4): pending / sync failed / synced with "Last synced" and an "Open in
 * Google Classroom" link. Copied from the classroom status card of
 * arcmosaico-prototype/components/Projects/ActivityDetailsModal.tsx
 * (L299-355), adapted Mantine v9→v6.
 */

import { useTranslation } from 'react-i18next'
import { Anchor, Badge, Group, Loader, Paper, Stack, Text } from '@mantine/core'
import { IconAlertTriangle, IconExternalLink } from '@tabler/icons-react'

import { GoogleClassroomLogo } from 'components/GoogleClassroomLogo'
import { ClassroomAssignmentState } from 'integrations/classroom'

function formatSyncedAt(iso: string, locale: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

interface ClassroomAssignmentStatusProps {
  readonly state: ClassroomAssignmentState
}

export function ClassroomAssignmentStatus({ state }: ClassroomAssignmentStatusProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || 'pt-BR'
  const tr = (key: string) => t(`classroom.${key}`)

  // `pending` means the worker is still creating the coursework: keep the
  // status visible and self-explanatory instead of an empty/ambiguous card
  // (Apple HIG: always tell the user what is happening).
  const failureKey =
    state.failureReason === 'not_course_teacher' ? 'assignmentNotCourseTeacher' : 'syncToClassroomFailed'

  return (
    <Paper withBorder p='sm' radius='md'>
      <Stack spacing='xs'>
        <Group position='apart' align='center' noWrap>
          <Group spacing='xs' noWrap>
            <GoogleClassroomLogo size={18} />
            <Text size='sm' weight={600}>
              {tr('googleClassroom')}
            </Text>
          </Group>
          {state.status === 'failed' && (
            <Badge color='red' leftSection={<IconAlertTriangle size={12} />}>
              {tr(failureKey)}
            </Badge>
          )}
          {state.status === 'pending' && (
            <Badge color='gray' leftSection={<Loader size={10} />}>
              {tr('classroomSyncStatusPending')}
            </Badge>
          )}
        </Group>
        {state.status === 'created' && (
          <Group spacing='sm' align='center'>
            {state.lastSubmissionsSyncAt && (
              <Text size='xs' color='dimmed'>
                {`${tr('classroomSyncLastSynced')}: ${formatSyncedAt(state.lastSubmissionsSyncAt, locale)}`}
              </Text>
            )}
            {state.alternateLink && (
              <Anchor href={state.alternateLink} target='_blank' rel='noopener noreferrer' size='xs'>
                <Group spacing={4} noWrap>
                  <IconExternalLink size={12} />
                  {tr('openInGoogleClassroom')}
                </Group>
              </Anchor>
            )}
          </Group>
        )}
      </Stack>
    </Paper>
  )
}
